import React, { useContext, useState } from "react"

import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import { Grid } from "@mui/material"
import { Tab, Tabs } from "@synapse-analytics/synapse-ui"

import { KonanTextEditor } from "../../../../components/data-display/KonanTextEditor"
import type { RootState } from "../../../../store/ReduxStore"
import { getEnvVar } from "../../../../utils/genericHelpers"
import { DecisionsContext } from "../../context/Context"
import { omitSatisfiedFlowPath } from "../../utils/helpers"
import { FuzzyMatchResultsTable } from "../FuzzyMatch/FuzzyMatchResultsTable"
import { WorkFlowPath } from "../WorkFlowPath/WorkFlowPath"

import styles from "./Canvas.module.scss"

/**
 * DecisionAnalysis
 * Four tabs:
 * - Decision Input
 * - Decision Output
 * - Workflow Path
 * - Fuzzy Match Results
 */
export function DecisionAnalysis(): React.ReactElement {
  const { id: projectId } = useParams<{ id: string }>()
  const { selectedRow } = useContext(DecisionsContext)
  const { t } = useTranslation("decisions", { keyPrefix: "decisionAnalysis" })

  const currentProject = useSelector((state: RootState) => state.project.currentProject)
  const isWorkflowProject = currentProject?.category === "workflow"

  type TabKey = "input" | "output" | "workflowPath" | "fuzzyMatch"
  const [activeTab, setActiveTab] = useState<TabKey>(isWorkflowProject ? "workflowPath" : "input")

  const isFuzzyMatchEnabled = getEnvVar("FUZZY_MATCH_ENABLED") === "true"

  const getInputFeatures = (): Record<string, unknown> => {
    if (selectedRow?.features_json) return selectedRow.features_json

    const rawFeatures = selectedRow?.features
    if (!rawFeatures || typeof rawFeatures !== "string") return {}

    try {
      return JSON.parse(rawFeatures) as Record<string, unknown>
    } catch {
      return {}
    }
  }

  const tabs: { key: TabKey; label: string }[] = [
    { key: "input", label: t("tabs.input") },
    { key: "output", label: t("tabs.output") },
    ...(isWorkflowProject ? [{ key: "workflowPath" as TabKey, label: t("tabs.workflowPath") }] : []),
    ...(isWorkflowProject && isFuzzyMatchEnabled ? [{ key: "fuzzyMatch" as TabKey, label: t("tabs.fuzzyMatch") }] : []),
  ]

  return (
    <div className={styles.decisionAnalysisContainer}>
      {/* Tabs navigation */}
      <Grid item xs={12} className={styles.tabsHeader}>
        <Tabs value={activeTab} className="base-tabs">
          {tabs.map(({ key, label }) => (
            <Tab key={key} label={label} value={key} selected={activeTab === key} onClick={() => setActiveTab(key)} />
          ))}
        </Tabs>
      </Grid>

      {/* Tab content */}
      <Grid item xs={12} className={styles.tabContent}>
        {activeTab === "input" && (
          <KonanTextEditor title={t("editor.input")} value={getInputFeatures()} language="json" />
        )}
        {activeTab === "output" && (
          <KonanTextEditor
            title={t("editor.output")}
            value={
              isWorkflowProject
                ? omitSatisfiedFlowPath(selectedRow?.workflow_output)
                : (selectedRow?.mls_output_json ?? {})
            }
            language="json"
          />
        )}
        {isWorkflowProject && activeTab === "workflowPath" && (
          <WorkFlowPath
            satisfiedFlowPath={selectedRow?.workflow_output?.satisfied_flow_path}
            workflowDetails={selectedRow?.workflow_output?.workflow}
          />
        )}
        {isWorkflowProject && isFuzzyMatchEnabled && activeTab === "fuzzyMatch" && (
          <FuzzyMatchResultsTable projectId={projectId as string} prediction_uuid={selectedRow?.uuid} />
        )}
      </Grid>
    </div>
  )
}
